/**
 * {order}の作成画面 /orders/:id（#233、docs/93 §4.3、Figma ①〜①-4）。見積書のエディタ（Estimate.tsx の Editor）を請求書と同じ形で使う
 *   上から 頭の帯（1段目＝‹・題・状態チップ・自動保存・↶ ↷・発行・⋯／2段目＝{vendor} 御中・発行日・No.・件名・{project} ›・元 ›・合計）→
 *   条件の帯（1行・押すと広がる）→ 明細（社内用の列なし・field の会社は行の詳細に内訳4つ）→ 送信設定の帯（明細の下・押すと上へ開く）
 *   ・頭と条件の自動保存は共通の仕組み（lib/saveQueue.tsx）。明細と同じ版を1本ずつ送る（409 ORDER_STALE＝「他の利用者が変更しました」）
 *   ・発行前の確認（§5.2）：要修正（止める）・注意（出せる）・参考（出せる）をチップの文字で分ける
 *   ・スマホ版と 767px 以下は閲覧のみ（作るのは PC。PC_ONLY_FEATURES の orderCreate）
 *   ・訂正版（#233 2b、docs/93 §14.5）：承諾済の{order}の「⋯」に「訂正版作成」→ 枝番の下書き（明細も条件も直せる）。回の帯（RoundStrip）は2回目から。
 *     発行前の確認に「前回とのちがい」（条件の変更を1行ずつ＋明細）・前回と同じ内容は発行できない。下書きの削除はその回だけ
 *   ・#250〜#255（docs/93 §16）：条件の「未定」はチェックだけ（付けると欄の値が空・欄は押せない）・変更協議は建設の{order}でその1回のスイッチ
 *   ・#256〜#262（docs/93 §17）：条件の帯は2列・1項目1行（Figma ②）・{orderAck}のスイッチと受け取り方（電子承諾｜書面）はその1回＝下書きの条件 order_terms.ack・
 *     約款の行は会社が約款オンのとき（その1回のオン／オフ）・変更協議は約款オンのとき・未定の理由と予定日は「理由…」の窓。
 *     発行前の確認は承諾を選ばず「{orderAck}　書面」の1行（変えるのは帯）
 */
import {
  ORDER_CHECK_MSG,
  ORDER_BUNDLE_MSG,
  ORDER_MSG,
  ORDER_REVISE_MSG,
  RFQ_MSG,
  ORDER_ACK_LABEL,
  ORDER_ACK_RECEIVE,
  ORDER_NEGOTIATION_DEFAULT,
  ORDER_PAY_METHOD_LABEL,
  ORDER_SETTINGS_MSG,
  OrderPayMethod,
  orderAckInitial,
  orderAckTurnOn,
  orderNegotiationOn,
  orderPayDays,
  orderStampNeeded,
  saveGateText,
  unissuedReissue,
  unissuedTitle,
  type Company,
  type GroupList,
  type LineInput,
  type Order,
  type OrderAckMode,
  type OrderCheck,
  type OrderCheckItem,
  type OrderIssueResult,
  type OrderRevisionDiff,
  type OrderTbdKey,
  type OrderTerms,
  type SheetInput,
  type UnissueResult,
  headRecipientOf,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { ME_KEY, useMe } from "../../auth.js";
import { DeleteDraftDialog } from "../../components/DeleteDraftDialog.jsx";
import { namePageNow } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { RoundStrip } from "../../components/RoundStrip.jsx";
import { DocField, subjWidth } from "../../components/DocHead.jsx";
import { HeadActions, type HeadAction } from "../../components/HeadActions.jsx";
import { FoldBand } from "../../components/FoldBand.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { UnissueDialog } from "../../components/UnissueDialog.jsx";
import { ErrorText, Field, Help, Switch, useToast } from "../../components/ui.jsx";
import { hasModule } from "../../core/slots.jsx";
import { t } from "../../labels.js";
import { mdSlash, yen } from "../../lib/num.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { currentView } from "../../lib/view.js";
import { useSaveQueue } from "../../lib/saveQueue.jsx";
import { Editor, type InvoiceBackend } from "../Estimate.jsx";
import { loadSendPrefs, saveSendPrefs, type OrderSendPrefs } from "./sendPrefs.js";
import { OrderStateChip } from "./parts.jsx";
import { OrderChanges } from "./revise.jsx";

type Head = { subject: string; issueDate: string; place: string; notes: string; terms: OrderTerms; groupId: string | null };
const headOf = (o: Order): Head => ({ subject: o.revision.subject, issueDate: o.revision.issueDate, place: o.revision.siteAddress, notes: o.revision.notes, terms: o.revision.terms, groupId: o.projectGroup?.id ?? null });

export function OrderPage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["order", id], queryFn: () => api<Order>("GET", `/orders/${id}`), retry: false });
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/orders">{t(ORDER_MSG.list)}</Link>
        </p>
      </div>
    );
  }
  // 回が変わったら（訂正版の作成・発行・下書きの削除）作り直す
  return <OrderEditor key={`${q.data.id}:${q.data.status}:${q.data.revision.id}${q.data.revising ? ":r" : ""}`} initial={q.data} />;
}

function OrderEditor({ initial }: { initial: Order }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const { toast, node: toastNode } = useToast();
  const narrow = useNarrow();
  const phone = narrow || currentView() === "phone";
  const field = hasModule(me.data, "field");
  // 1回目の下書きと訂正版の下書き（#233 2b）だけ直せる
  const revising = !!initial.revising;
  const readOnly = (initial.status !== "draft" && !revising) || phone;
  /** #315：頭の宛先（画面は「略称 ／ 支店 ／ 担当者」・乗せると紙の宛名）。出したあとは固めた担当者の名前 */
  const rcp = headRecipientOf({
    name: initial.client.name,
    shortName: initial.client.shortName ?? "",
    honorific: initial.client.honorific || "御中",
    branch: initial.client.branch?.name ?? null,
    contact: initial.issued?.recipientContact ?? (initial.client.contact ? `${initial.client.contact.department} ${initial.client.contact.name}`.trim() : null),
  });
  const no = initial.displayNo ?? initial.documentNo ?? "";
  const title = initial.names.order;
  const cur = (qc.getQueryData(["order", initial.id]) as Order | undefined) ?? initial;
  const [head, setHead] = useState<Head>(() => headOf(initial));
  // {project}の選び直し（下書きだけ）：完了した{project}は出さない（いま付いているものは出す）
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all"), enabled: !readOnly });
  const groupChoices: Array<{ id: string; name: string }> = (groups.data?.items ?? []).filter((g) => !g.completedAt || g.id === head.groupId);
  if (cur.projectGroup && !groupChoices.some((g) => g.id === cur.projectGroup!.id)) groupChoices.unshift({ id: cur.projectGroup.id, name: cur.projectGroup.name });
  const [open, setOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [prefs, setPrefs] = useState<OrderSendPrefs>(() => loadSendPrefs(me.data?.session.accountId ?? "", initial));
  // 条件の帯の小さな窓（未定の理由と予定日・変更協議の文。#258）
  const [bandDlg, setBandDlg] = useState<BandDlg | null>(null);
  const updPrefs = (p: Partial<OrderSendPrefs>) =>
    setPrefs((x) => {
      const n = { ...x, ...p };
      if (n.remember) saveSendPrefs(me.data?.session.accountId ?? "", n);
      return n;
    });

  /* ---- 版：明細（Editor）と頭・条件は同じ PUT /orders/:id を別々の列で送る。版は1つ・1本ずつ（請求書と同じ） ---- */
  const ver = useRef(initial.updatedAt);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const serial = <T,>(fn: () => Promise<T>): Promise<T> => {
    const p = chain.current.then(fn, fn);
    chain.current = p.catch(() => undefined);
    return p;
  };
  const staleRef = useRef(false);
  const openStale = useRef<() => void>(() => undefined);
  const headBase = useRef<Head>(headOf(initial));
  const latest = useRef(head);
  latest.current = head;
  const hq = useSaveQueue({
    send: async () => {
      const h = latest.current;
      const o = await serial(() => api<Order>("PUT", `/orders/${initial.id}`, { subject: h.subject, issueDate: h.issueDate, place: h.place, notes: h.notes, terms: h.terms, projectGroupId: h.groupId, version: ver.current }));
      ver.current = o.updatedAt;
      headBase.current = headOf(o);
      qc.setQueryData(["order", initial.id], o);
      void qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "ORDER_STALE") {
        staleRef.current = true;
        openStale.current();
        return { msg: ORDER_MSG.stale, then: "stop" };
      }
    },
    blocked: () => staleRef.current,
  });
  const setH = (patch: Partial<Head>, immediate = false) => {
    setHead((h) => ({ ...h, ...patch }));
    latest.current = { ...latest.current, ...patch };
    hq.mark(immediate);
  };
  const setT = (patch: Partial<OrderTerms>, immediate = true) => setH({ terms: { ...latest.current.terms, ...patch } }, immediate);
  // この1回の承諾（#257）：条件の帯の選び（下書きの order_terms.ack）→ {vendor}の承諾方法 → 会社の既定（端末に覚えない）
  const ack: OrderAckMode = orderAckInitial(cur.ack.company, cur.ack.partner, head.terms.ack);
  const linesReady = useRef<() => Promise<string | null>>(async () => null);
  const register = useCallback((f: () => Promise<string | null>) => {
    linesReady.current = f;
  }, []);
  const readyAll = async (): Promise<string | null> => (await linesReady.current()) ?? (await hq.ready());

  /* ---- 発行 ---- */
  const needsCompany = !!me.data?.tenant.needsCompanyName;
  const [dlg, setDlg] = useState<IssueDlg | null>(null);
  const openIssue = async () => {
    const why = await readyAll();
    if (why != null) {
      toast(saveGateText("issue", why), undefined, "error");
      return;
    }
    try {
      const check = await api<OrderCheck>("POST", `/orders/${initial.id}/check`, {});
      // 訂正版（#233 2b）：前回とのちがい（条件の変更と明細）
      const diff = revising ? await api<OrderRevisionDiff>("GET", `/orders/${initial.id}/diff`) : undefined;
      let company = "";
      if (needsCompany) {
        try {
          company = (await api<Company>("GET", "/company")).name;
        } catch {
          /* 空のまま */
        }
      }
      setDlg({ check, company, busy: false, diff });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const doIssue = async () => {
    if (!dlg) return;
    if (needsCompany && !dlg.company.trim()) {
      setDlg({ ...dlg, error: t(ORDER_MSG.companyRequired) });
      return;
    }
    setDlg({ ...dlg, busy: true, error: undefined });
    try {
      const why = await readyAll();
      if (why != null) {
        setDlg({ ...dlg, busy: false, error: saveGateText("issue", why) });
        return;
      }
      const r = await api<OrderIssueResult>("POST", `/orders/${initial.id}/issue`, {
        ack,
        print: { breakdown: prefs.printBreakdown, seal: prefs.printSeal },
        ...(needsCompany ? { company: { name: dlg.company.trim() } } : {}),
        version: ver.current,
      });
      setDlg(null);
      hq.discard();
      void qc.invalidateQueries({ queryKey: ME_KEY });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      await qc.invalidateQueries({ queryKey: ["order", initial.id] });
      const notice = revising ? ORDER_REVISE_MSG.issued.replace("{no}", r.documentNo) : t(ORDER_MSG.issued).replace("{no}", r.documentNo);
      // 発行したら紙の画面（送付）へ。送信方法は送信設定の帯で選んだもの（docs/91 §15 と同じ）
      // リンクで送るのは電子のときだけ（なし・紙で発行したら印刷・FAX）
      namePageNow(title, docShort(title, r.documentNo)); // 送信の画面の戻るボタンに番号（訂正版は枝番。読み直しで編集の画面が作り直されても、移る前に付ける）
      nav(`/orders/${initial.id}/paper`, { state: { notice, noticeTestId: "issued-notice", via: prefs.via === "link" && ack !== "electronic" ? "print" : prefs.via } });
    } catch (e) {
      if (e instanceof ApiError && e.code === "ORDER_CHECK_FIX") {
        setDlg({ ...dlg, busy: false, check: { canIssue: false, items: (e.problem as { items?: OrderCheckItem[] }).items ?? dlg.check.items } });
        return;
      }
      // 前回と同じ内容（ほかの画面で戻した）：ちがいを読み直す
      if (e instanceof ApiError && e.code === "REVISE_NO_DIFF") {
        const diff = await api<OrderRevisionDiff>("GET", `/orders/${initial.id}/diff`).catch(() => dlg.diff);
        setDlg((d) => (d ? { ...d, busy: false, diff, error: messageOf(e) } : d));
        return;
      }
      setDlg((d) => (d ? { ...d, busy: false, error: messageOf(e) } : d));
    }
  };
  /** 確かめの「入力」：窓を閉じて条件の帯を広げ、その欄へ（約款は標準を付ける・変更協議は文の窓） */
  const goField = (a: OrderCheckItem["action"]) => {
    setDlg(null);
    setOpen(true);
    if (a === "terms") setT({ terms: "standard" });
    if (a === "negotiation") {
      setBandDlg({ kind: "nego", text: latest.current.terms.negotiation });
      return;
    }
    window.setTimeout(() => (a === "terms" ? document.querySelector<HTMLElement>('[data-testid="oc-terms-on"]') : document.getElementById(a ? `oc_${a}` : "oc_receive"))?.focus(), 60);
  };

  /* ---- 取消し（取消しの窓＝見積書・請求書と同じ UnissueDialog。#233 §13.7 ハ）・削除 ---- */
  const [askDelete, setAskDelete] = useState(false);
  const [unissueDlg, setUnissueDlg] = useState<{ busy: boolean; error?: string | undefined } | null>(null);
  const doUnissue = async () => {
    setUnissueDlg({ busy: true });
    try {
      const r = await api<UnissueResult>("POST", `/orders/${initial.id}/unissue`, {});
      setUnissueDlg(null);
      toast(t(ORDER_MSG.unissued).replace("{no}", r.documentNo), undefined, "success", "unissued-notice");
      void qc.invalidateQueries({ queryKey: ["orders"] });
      await qc.invalidateQueries({ queryKey: ["order", initial.id] });
    } catch (e) {
      setUnissueDlg({ busy: false, error: messageOf(e) });
    }
  };
  // 窓は components/DeleteDraftDialog（見積書と同じ形。docs/96 §13.5）＝断られたら窓の中に文が出る
  const deleteDraft = async () => {
    await api<void>("DELETE", `/orders/${initial.id}`);
    hq.discard();
    void qc.invalidateQueries({ queryKey: ["orders"] });
    if (revising) {
      // 訂正版の下書き（#233 2b）：その回だけ消える。出ている回の画面に戻る
      setAskDelete(false);
      toast(ORDER_REVISE_MSG.discarded.replace("{n}", String(initial.revision.no)), undefined, "success", "revision-discarded");
      void qc.invalidateQueries({ queryKey: ["order-rounds", initial.id] });
      await qc.invalidateQueries({ queryKey: ["order", initial.id] });
      return;
    }
    nav(initial.projectGroup ? `/groups/${initial.projectGroup.id}?tab=orders` : "/orders", { replace: true });
  };
  /** 訂正版を作成（#233 2b）：承諾済の{order}だけ。作ると枝番の下書き（画面はその回を直す） */
  const revise = async () => {
    try {
      const o = await api<Order>("POST", `/orders/${initial.id}/revise`, {});
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["order-rounds", initial.id] });
      qc.setQueryData(["order", initial.id], o);
      toast(ORDER_REVISE_MSG.reviseDone.replace("{no}", o.displayNo ?? ""), undefined, "success", "revise-created");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };

  // 比較（#233 2b）：2回目から。訂正中は 出ている回 → 下書き、出したあとは 前の回 → 今の回
  const compareTo = initial.revision.no >= 2 ? `/orders/${initial.id}/compare?from=${initial.revision.no - 1}&to=${initial.revision.no}` : null;
  const actions: HeadAction[] = readOnly
    ? [
        { label: "PDF 表示", to: `/orders/${initial.id}/paper`, testId: "order-paper", rank: 1 },
        ...(initial.canUnissue ? [{ label: ORDER_MSG.unissue, onSelect: () => setUnissueDlg({ busy: false }), testId: "order-unissue", group: "state" as const }] : []),
        ...(initial.canRevise && !phone ? [{ label: ORDER_REVISE_MSG.revise, onSelect: () => void revise(), testId: "order-revise", group: "state" as const }] : []),
        ...(compareTo ? [{ label: ORDER_REVISE_MSG.compare, to: compareTo, testId: "order-compare", group: "doc" as const }] : []),
      ]
    : [
        { label: ORDER_MSG.pdfDraft, onSelect: () => void readyAll().then((why) => (why == null ? nav(`/orders/${initial.id}/paper`) : toast(saveGateText("pdf", why), undefined, "error"))), testId: "order-pdf-draft", group: "doc" as const },
        ...(compareTo ? [{ label: ORDER_REVISE_MSG.compare, to: compareTo, testId: "order-compare", group: "doc" as const }] : []),
        { label: ORDER_MSG.deleteDraft, onSelect: () => setAskDelete(true), testId: "order-delete", group: "danger" as const },
      ];

  const backend: InvoiceBackend = {
    doc: {
      kind: "order",
      title,
      queryKey: "order",
      listKey: "orders",
      back: initial.projectGroup ? { to: `/groups/${initial.projectGroup.id}?tab=orders`, label: initial.projectGroup.name } : { to: "/orders", label: t(ORDER_MSG.list) },
      labor: field && initial.construction,
    },
    save: (body: { sheets: SheetInput[]; lines: LineInput[] }) =>
      serial(async () => {
        const o = await api<Order>("PUT", `/orders/${initial.id}`, { ...body, version: ver.current });
        ver.current = o.updatedAt;
        // 回の帯の下書きの合計（#233 2b）
        if (revising) void qc.invalidateQueries({ queryKey: ["order-rounds", initial.id] });
        return o;
      }),
    version: () => ver.current,
    reload: () => api<Order>("GET", `/orders/${initial.id}`),
    stale: () => {
      staleRef.current = true;
    },
    registerStale: (o) => {
      openStale.current = o;
    },
    synced: (e) => {
      const theirs = headOf(e as Order);
      const base = headBase.current;
      const mine = latest.current;
      const merged: Head = { ...theirs };
      for (const k of Object.keys(theirs) as Array<keyof Head>) {
        if (JSON.stringify(mine[k]) !== JSON.stringify(base[k])) (merged as Record<keyof Head, unknown>)[k] = mine[k];
      }
      ver.current = e.updatedAt;
      headBase.current = theirs;
      latest.current = merged;
      setHead(merged);
      staleRef.current = false;
      if (JSON.stringify(merged) !== JSON.stringify(theirs)) hq.mark(true);
      else {
        hq.discard();
        hq.setState((x) => (x.kind === "error" ? { kind: "idle" } : x));
      }
    },
    register,
    actions: null,
    // #245（docs/94 B-4 案B）：頭は DocHead の3段。2段目＝{order}の絵と名前・件名（その場で修正）・状態・？｜合計。3段目＝宛先・発行日・No.・{project}・元
    docHead: {
      kind: "orders",
      kindLabel: initial.names.order,
      title: (
        <input
          id="subj"
          className="dh-subj" style={subjWidth(head.subject)}
          aria-label={ORDER_MSG.colSubject}
          placeholder={ORDER_MSG.colSubject}
          value={head.subject}
          readOnly={readOnly}
          onChange={(e) => setH({ subject: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          data-testid="order-subject"
        />
      ),
      primary: (
        <span className="hd-acts" data-testid="head-actions">
          {readOnly ? null : (
            <button className="btn primary" type="button" onClick={() => void openIssue()} data-testid="order-issue">
              {revising ? ORDER_REVISE_MSG.issue.replace("{no}", no) : ORDER_MSG.issue}
            </button>
          )}
          <HeadActions actions={actions} label={t(ORDER_MSG.stateMenu)} part="main" />
        </span>
      ),
      menu: <HeadActions actions={actions} label={t(ORDER_MSG.stateMenu)} part="more" />,
      meta: (
        <>
          <DocField label="宛先">
            {/* #315：画面は「略称 ／ 支店 ／ 担当者」（敬称なし）。乗せると紙の宛名（正式名称・敬称つき） */}
            <span className="dh-to" title={rcp.paper}>
              <b data-testid="order-vendor">{rcp.name}</b>
              {rcp.branch ? <span className="brn"> ／ {rcp.branch}</span> : null}
              {rcp.contact ? <span className="brn"> ／ {rcp.contact}</span> : null}
            </span>
          </DocField>
          <DocField label="発行日">
            <DateInput inline label="発行日" ariaLabel="発行日" value={head.issueDate} readOnly={readOnly} clearable={false} onChange={(v) => v && setH({ issueDate: v }, true)} testId="issue-date" />
          </DocField>
          <DocField label="No.">
            <span className="num" data-testid="order-no">{initial.displayNo ?? initial.documentNo ?? "未発行"}</span>
          </DocField>
          <DocField label={<span id="ogroup-l">{t("{project}")}</span>}>
            {readOnly ? (
              cur.projectGroup ? <Link to={`/groups/${cur.projectGroup.id}`} data-testid="order-group">{cur.projectGroup.name} ›</Link> : <span className="muted">—</span>
            ) : (
              <>
                {/* 下書きのあいだは{project}を選び直せる（{project}なしで作ったものをあとから付ける・外す。docs/93 §4.2） */}
                <select className="inp ogroupsel" aria-labelledby="ogroup-l" value={head.groupId ?? ""} onChange={(e) => setH({ groupId: e.target.value || null }, true)} data-testid="order-group-select">
                  <option value="">—</option>
                  {groupChoices.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                {cur.projectGroup && cur.projectGroup.id === head.groupId ? <Link to={`/groups/${cur.projectGroup.id}`} aria-label={`${cur.projectGroup.name}を開く`} data-testid="order-group">›</Link> : null}
              </>
            )}
          </DocField>
          {/* 元（#233 2a）：見積依頼が2件以上（まとめて作成）なら「見積依頼 n件 ›」。1件なら{vendorQuote}・依頼・見積のどれか1つ */}
          {initial.origins.rfqs.length > 1 ? (
            <DocField label={ORDER_MSG.source}>
              <Link to={`/rfqs?f=all&o=${initial.id}`} data-testid="order-source-rfqs">{ORDER_BUNDLE_MSG.originRfqs.replace("{n}", String(initial.origins.rfqs.length))} ›</Link>
            </DocField>
          ) : initial.source ? (
            <DocField label={ORDER_MSG.source}>
              <Link to={`/vendor-quotes/${initial.source.vendorQuoteId}`} data-testid="order-source">{t("{vendorQuote}")} No.{initial.source.quoteNo} ›</Link>
            </DocField>
          ) : initial.origins.rfqs[0] ? (
            <DocField label={ORDER_MSG.source}>
              <Link to={`/rfqs/${initial.origins.rfqs[0].id}`} data-testid="order-source-rfq">{`${RFQ_MSG.title} ${initial.origins.rfqs[0].no ?? ""}`.trim()} ›</Link>
            </DocField>
          ) : initial.origins.estimates[0] ? (
            <DocField label={ORDER_MSG.source}>
              <Link to={`/estimates/${initial.origins.estimates[0].id}`} data-testid="order-source-estimate">{`見積書 ${initial.origins.estimates[0].no ?? ORDER_MSG.chipDraft}`} ›</Link>
            </DocField>
          ) : null}
        </>
      ),
    },
    chips: <OrderStateChip o={cur} />,
    help: [
      // #276：見出しのチップ（番号ありの下書き）の title と同じ1行
      cur.ackState === "draft" && cur.documentNo && !cur.revising ? `${unissuedTitle(cur.documentNo)}。` : null,
      t("明細の金額は{vendor}への仕入れそのものです（社内用の列はありません）。"),
      initial.construction
        ? initial.basicContract
          ? t("基本契約書の記録がある{vendor}の{order}は、約款を付けず、基本契約書による1行の紙になります。")
          : t("建設工事の{order}は、約款を後ろに付けた紙になります（約款添付型）。")
        : t("条件の表は取適法の明示の事項（受領期日・納品場所・検査完了期日・支払期日・支払方法）です。"),
      phone ? t("スマホでは閲覧のみです。修正はパソコンで行います。") : null,
    ],
    head: ({ total }) => (
      <>
        {/* 回の帯（#233 2b）：2回目から。前の回には控えの PDF と承諾の記録（{orderAck}・請書の写し） */}
        {initial.revision.no >= 2 ? (
          <RoundStrip
            estimateId={initial.id}
            doc="orders"
            label={`この${initial.names.order}の回`}
            expected={{ no: initial.revision.no, revising }}
            previousExtra={(r) =>
              r.ack ? (
                <a className="btn text rcopy" href={r.ack.method === "paper" && r.ack.hasCopy ? `/api/v1/orders/${initial.id}/ack/copy?rev=${r.no}` : `/api/v1/orders/${initial.id}/ack.pdf?rev=${r.no}`} target="_blank" rel="noreferrer" data-testid={`round-ack-${r.no}`}>
                  {r.ack.method === "paper" && r.ack.hasCopy ? ORDER_REVISE_MSG.roundPaperCopy : initial.names.orderAck}
                </a>
              ) : null
            }
          />
        ) : null}
        <TermsBand o={cur} head={head} readOnly={readOnly} open={open} setOpen={setOpen} setT={setT} setPlace={(v) => setH({ place: v })} ack={ack} dlg={bandDlg} setDlg={setBandDlg} />
        <ErrorText>{hq.state.kind === "error" ? hq.state.msg : undefined}</ErrorText>
      </>
    ),
    footer: (
      <>
        {readOnly ? null : <SendBand o={cur} prefs={prefs} upd={updPrefs} open={sendOpen} setOpen={setSendOpen} field={field} ack={ack} />}
        <div className="paper memo">
          <div className="field">
            <label htmlFor="memo">備考（紙の下に印字されます）</label>
            <textarea id="memo" value={head.notes} readOnly={readOnly} onChange={(e) => setH({ notes: e.target.value })} />
          </div>
        </div>
      </>
    ),
  };

  return (
    <>
      <Editor initial={initial} invoice={backend} />
      {hq.dialog}
      {dlg ? <IssueDialog o={cur} dlg={dlg} setDlg={setDlg} needsCompany={needsCompany} ack={ack} onIssue={() => void doIssue()} onGo={goField} /> : null}
      {askDelete ? (
        <DeleteDraftDialog
          prefix="order"
          body={revising ? t(ORDER_REVISE_MSG.discardAsk).replace("{n}", String(initial.revision.no)).replace("{prev}", String(initial.revising?.baseRevisionNo ?? 1)).replace("{no}", initial.prevRound?.displayNo ?? "") : undefined}
          confirmLabel={revising ? ORDER_REVISE_MSG.discardConfirm.replace("{n}", String(initial.revision.no)) : undefined}
          onClose={() => setAskDelete(false)}
          onConfirm={deleteDraft}
        />
      ) : null}
      {unissueDlg ? <UnissueDialog doc="order" no={initial.displayNo ?? initial.documentNo ?? ""} busy={unissueDlg.busy} error={unissueDlg.error} onClose={() => setUnissueDlg(null)} onConfirm={() => void doUnissue()} /> : null}
      {toastNode}
    </>
  );
}

/* ============================================================================
 * 条件の帯（#258、docs/93 §17、Figma ② 255:189・②-2 255:307）。閉じた1行は要約（未定の項目は出さない）、開くと 1項目1行（56px）の2列：
 *   左＝受領期日〜・納品場所・検査完了期日・支払期日（未定の欄は薄い地に「未定」で押せない・右端に「未定」・未定のとき「理由…」から小さな窓）
 *   右＝支払方法・{orderAck}（スイッチ＋電子承諾｜書面＝その1回。#257）・約款（会社が約款オンか、この{order}の約款がオンのとき。#261）・変更協議（約款オンのとき）
 *   営業力（建設でない）の右の列は 支払方法・{orderAck} の2行。帯の幅 1320px 未満は1列
 * ========================================================================== */
type BandDlg = { kind: "tbd"; key: OrderTbdKey; reason: string; on: string | null } | { kind: "nego"; text: string };
function TermsBand({ o, head, readOnly, open, setOpen, setT, setPlace, ack, dlg, setDlg }: {
  o: Order;
  head: Head;
  readOnly: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
  setT: (p: Partial<OrderTerms>, immediate?: boolean) => void;
  setPlace: (v: string) => void;
  /** この1回の承諾（下書きの選び → {vendor} → 会社） */
  ack: OrderAckMode;
  dlg: BandDlg | null;
  setDlg: (d: BandDlg | null) => void;
}) {
  const t0 = head.terms;
  const md = (d: string | null) => (d ? `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}` : "");
  // 未定（#251）：チェックだけで通す（理由と予定日は任意）
  const tbd = (k: OrderTbdKey) => !!t0.tbd[k];
  const shu = (v: string) => <span className="shu">{v}</span>;
  const pd = orderPayDays(t0);
  const receive = t0.receiveFrom || t0.receiveTo ? `${md(t0.receiveFrom)}${t0.receiveFrom && t0.receiveTo ? "〜" : ""}${md(t0.receiveTo)}` : "";
  // 自社の約款の名前：出した回は固めた名前・下書きは会社の今の PDF の名前（下書きの termsName は作った時の会社の名前で、あてにならない）
  const issuedTerms = o.status !== "draft" && !o.revising;
  const ownName = (issuedTerms && t0.termsName) || o.companyTerms.ownName || ORDER_SETTINGS_MSG.termsOwn;
  const termsText = t0.terms === "standard" ? ORDER_SETTINGS_MSG.termsStandard : t0.terms === "own" ? ownName : ORDER_MSG.termsNone;
  // 約款と変更協議の行（#261）：建設の{order}だけ。約款は会社が約款オンか、この{order}の約款がオンのとき（基本契約書の記録があれば表示だけ）。
  // 変更協議は約款オンのとき（基本契約書の記録があれば会社が約款オンのとき）
  const termsRow = o.construction && !o.basicContract && (o.companyTerms.kind !== "none" || t0.terms !== "none");
  const negoRow = o.construction && (o.basicContract ? o.companyTerms.kind !== "none" : t0.terms !== "none");
  const negoOn = negoRow && orderNegotiationOn(t0);
  const negoText = t0.negotiation.trim() === (o.companyTerms.negotiation || ORDER_NEGOTIATION_DEFAULT).trim() ? ORDER_MSG.negotiationShort : ORDER_MSG.negotiationEdited;
  const ackName = o.names.orderAck;
  /** 未定を付ける・外す。付けると欄の値を空にする（未定が勝つ＝API の保存と同じ）。理由と予定日は任意 */
  const toggleTbd = (k: OrderTbdKey, on: boolean) => {
    const next = { ...t0.tbd };
    if (on) next[k] = { reason: "", on: null };
    else delete next[k];
    const clear: Partial<OrderTerms> = on ? (k === "receive" ? { receiveFrom: null, receiveTo: null } : k === "inspect" ? { inspectOn: null } : k === "pay" ? { payOn: null } : {}) : {};
    if (on && k === "place") setPlace("");
    setT({ ...clear, tbd: next });
  };
  /** 右端の「未定」（と未定のときの「理由…」）。読むだけは出さない */
  const tbdEnd = (k: OrderTbdKey) =>
    readOnly ? null : (
      <span className="otbdx">
        {tbd(k) ? (
          <button
            type="button"
            className={"btn text sm otbdnote" + (t0.tbd[k]?.reason || t0.tbd[k]?.on ? " is-set" : "")}
            onClick={() => setDlg({ kind: "tbd", key: k, reason: t0.tbd[k]?.reason ?? "", on: t0.tbd[k]?.on ?? null })}
            data-testid={`tbd-${k}-note`}
          >
            {ORDER_MSG.tbdNote}
          </button>
        ) : null}
        <label className="chk">
          <input type="checkbox" checked={tbd(k)} onChange={(e) => toggleTbd(k, e.target.checked)} data-testid={`tbd-${k}-check`} />
          {ORDER_MSG.tbd}
        </label>
      </span>
    );
  const row = (key: string, label: ReactNode, body: ReactNode, end?: ReactNode, labelFor?: string) => (
    <div className="orow" data-testid={`oc-row-${key}`}>
      {labelFor ? <label className="orl" htmlFor={labelFor}>{label}</label> : <span className="orl">{label}</span>}
      <span className="orv">{body}</span>
      {end}
    </div>
  );
  /** 閉じた1行の1項目（未定は出さない＝#258） */
  const sum = (k: OrderTbdKey, label: string, v: ReactNode) => (tbd(k) ? null : <span><span className="muted">{label}</span> {v || shu(ORDER_MSG.notEntered)}</span>);
  const payVal = t0.payOn ? (pd && pd.days > 60 ? shu(`${t0.payOn.replaceAll("-", "/")}（${pd.days}日）`) : md(t0.payOn)) : "";
  // #244（docs/94 B-5）：開閉の帯（FoldBand）。閉じた見出しの行の全体が「開く」
  return (
    <>
      <FoldBand
        id="order.terms"
        className="oterms"
        title={
          <>
            {ORDER_MSG.terms}
            {open && o.construction ? (
              <span className="chip ai" data-testid="order-construction">
                {o.basicContract ? ORDER_MSG.constructionChipBasic : ORDER_MSG.constructionChip}
              </span>
            ) : null}
          </>
        }
        summary={
          <>
            {sum("receive", ORDER_MSG.receive, receive)}
            {sum("place", ORDER_MSG.place, head.place)}
            {sum("inspect", ORDER_MSG.inspect, md(t0.inspectOn))}
            {sum("pay", ORDER_MSG.pay, payVal)}
            <span><span className="muted">{ORDER_MSG.payMethod}</span> {ORDER_PAY_METHOD_LABEL[t0.method]}</span>
            <span data-testid="order-terms-band-ack"><span className="muted">{ackName}</span> {ORDER_ACK_LABEL[ack]}</span>
            {/* 約款：約款の行が出る{order}だけ（基本契約書の記録のある{vendor}＝2c は「基本契約書」） */}
            {o.construction && o.basicContract ? (
              <span data-testid="order-terms-band-terms"><span className="muted">{ORDER_MSG.termsDoc}</span> {ORDER_MSG.basicContract}</span>
            ) : termsRow ? (
              <span data-testid="order-terms-band-terms"><span className="muted">{ORDER_MSG.termsDoc}</span> {termsText}</span>
            ) : null}
            {/* 変更協議（#255）：付ける回だけ */}
            {negoOn ? <span data-testid="order-terms-band-negotiation"><span className="muted">{ORDER_MSG.negotiation}</span> {negoText}</span> : null}
          </>
        }
        open={open}
        onToggle={setOpen}
        testId={open ? "order-terms-open" : undefined}
        closedTestId="order-terms-band"
        toggleTestId="order-terms-close"
      >
        <div className="ocols">
          <div className="ocol">
            {row(
              "receive",
              ORDER_MSG.receive,
              <>
                <DateInput label={`${ORDER_MSG.receive}（開始）`} ariaLabel={`${ORDER_MSG.receive}（開始）`} value={t0.receiveFrom ?? ""} readOnly={readOnly} disabled={tbd("receive")} placeholder={tbd("receive") ? ORDER_MSG.tbd : undefined} onChange={(v) => setT({ receiveFrom: v || null })} testId="oc-receive-from" />
                <span className="otilde">〜</span>
                <DateInput id="oc_receive" label={ORDER_MSG.receive} ariaLabel={ORDER_MSG.receive} value={t0.receiveTo ?? ""} readOnly={readOnly} disabled={tbd("receive")} placeholder={tbd("receive") ? ORDER_MSG.tbd : undefined} onChange={(v) => setT({ receiveTo: v || null })} testId="oc-receive-to" />
              </>,
              tbdEnd("receive"),
            )}
            {row(
              "place",
              ORDER_MSG.place,
              <>
                <input id="oc_place" className="inp oplace" value={head.place} readOnly={readOnly} disabled={tbd("place")} placeholder={tbd("place") ? ORDER_MSG.tbd : undefined} onChange={(e) => setPlace(e.target.value)} data-testid="oc-place" />
                {o.projectGroup?.siteAddress && !readOnly && !tbd("place") && head.place !== o.projectGroup.siteAddress ? (
                  <button type="button" className="btn text sm" onClick={() => setPlace(o.projectGroup!.siteAddress!)}>{t(ORDER_MSG.placeFromGroup)}</button>
                ) : null}
              </>,
              tbdEnd("place"),
              "oc_place",
            )}
            {row(
              "inspect",
              ORDER_MSG.inspect,
              <DateInput id="oc_inspect" label={ORDER_MSG.inspect} ariaLabel={ORDER_MSG.inspect} value={t0.inspectOn ?? ""} readOnly={readOnly} disabled={tbd("inspect")} placeholder={tbd("inspect") ? ORDER_MSG.tbd : undefined} onChange={(v) => setT({ inspectOn: v || null })} testId="oc-inspect" />,
              tbdEnd("inspect"),
            )}
            {row(
              "pay",
              ORDER_MSG.pay,
              <>
                <DateInput id="oc_pay" label={ORDER_MSG.pay} ariaLabel={ORDER_MSG.pay} value={t0.payOn ?? ""} readOnly={readOnly} disabled={tbd("pay")} placeholder={tbd("pay") ? ORDER_MSG.tbd : undefined} onChange={(v) => setT({ payOn: v || null })} testId="oc-pay" />
                {!tbd("pay") && (o.payTerms || pd) ? (
                  <span className="onote">
                    {o.payTerms ? <span data-testid="oc-pay-terms">{o.payTerms}</span> : null}
                    {pd ? <span className={pd.days > 60 ? "shu" : undefined} data-testid="oc-pay-days">{ORDER_MSG.payDaysNote.replace("{days}", String(pd.days))}</span> : null}
                  </span>
                ) : null}
              </>,
              tbdEnd("pay"),
            )}
          </div>
          <div className="ocol">
            {row(
              "method",
              ORDER_MSG.payMethod,
              <select id="oc_method" className="inp omethod" value={t0.method} disabled={readOnly} onChange={(e) => setT({ method: e.target.value as OrderTerms["method"] })} data-testid="oc-method">
                {OrderPayMethod.options.map((m) => (
                  <option key={m} value={m}>{ORDER_PAY_METHOD_LABEL[m]}</option>
                ))}
              </select>,
              undefined,
              "oc_method",
            )}
            {/* {orderAck}（#257）：その1回（初めの値は {vendor} → 会社）。オンにした瞬間は {vendor} → 会社の受け取り方（オフなら書面） */}
            {row(
              "ack",
              ackName,
              <>
                <Switch bare checked={ack !== "none"} disabled={readOnly} onChange={(v) => setT({ ack: v ? orderAckTurnOn(orderAckInitial(o.ack.company, o.ack.partner)) : "none" })} label={ackName} testId="oc-ack-on" />
                {ack !== "none" ? (
                  <Segmented
                    label={`${ackName} ${ORDER_SETTINGS_MSG.ackReceive}`}
                    value={ack}
                    disabled={readOnly}
                    onChange={(v) => setT({ ack: v })}
                    options={ORDER_ACK_RECEIVE.map((v) => ({ value: v, label: ORDER_ACK_LABEL[v], testId: `oc-ack-${v}` }))}
                  />
                ) : null}
              </>,
            )}
            {o.construction && o.basicContract ? (
              row(
                "terms",
                ORDER_MSG.termsDoc,
                <span data-testid="oc-basic-contract">
                  {ORDER_MSG.basicContract}　{o.basicContract.on.replace(/-/g, "/")} {o.basicContract.name} <Help text={t(ORDER_MSG.basicContractNote)} />
                </span>,
              )
            ) : termsRow ? (
              row(
                "terms",
                ORDER_MSG.termsDoc,
                <>
                  <Switch
                    bare
                    checked={t0.terms !== "none"}
                    disabled={readOnly}
                    // オンにすると会社の種類（会社がオフなら標準）・変更協議は会社の設定（会社がオフならオン）。オフにすると変更協議もオフ（#261）
                    onChange={(v) =>
                      setT(
                        v
                          ? { terms: o.companyTerms.kind === "none" ? "standard" : o.companyTerms.kind, termsName: o.companyTerms.kind === "own" ? o.companyTerms.ownName : "", negotiationOn: o.companyTerms.kind === "none" ? true : o.companyTerms.negotiationOn }
                          : { terms: "none", negotiationOn: false },
                      )
                    }
                    label={ORDER_MSG.termsDoc}
                    testId="oc-terms-on"
                  />
                  {t0.terms !== "none" ? (
                    <>
                      <span data-testid="oc-terms-name">{termsText}</span>
                      <a className="btn text sm" href="/api/v1/company/order-terms.pdf" target="_blank" rel="noreferrer" data-testid="oc-terms-show">{ORDER_MSG.termsShow}</a>
                    </>
                  ) : null}
                </>,
              )
            ) : null}
            {/* 変更協議（#255・#261）：約款オンのときだけ。その1回だけ付ける・外す（初めは会社の設定）。文は「修正 ›」の窓 */}
            {negoRow
              ? row(
                  "negotiation",
                  ORDER_MSG.negotiation,
                  <>
                    <Switch bare checked={negoOn} disabled={readOnly} onChange={(v) => setT({ negotiationOn: v })} label={ORDER_MSG.negotiation} testId="oc-negotiation-on" />
                    {negoOn ? (
                      <>
                        <span data-testid="oc-negotiation-state">{negoText}</span>
                        <button type="button" className="btn text sm" onClick={() => setDlg({ kind: "nego", text: t0.negotiation })} data-testid="oc-negotiation-edit">
                          {readOnly ? ORDER_MSG.negotiation + " ›" : ORDER_MSG.negotiationEdit}
                        </button>
                      </>
                    ) : null}
                  </>,
                )
              : null}
          </div>
        </div>
      </FoldBand>
      {dlg ? <BandDialog dlg={dlg} setDlg={setDlg} readOnly={readOnly} setT={setT} tbdOf={(k) => t0.tbd[k]} defaultNego={o.companyTerms.negotiation || ORDER_NEGOTIATION_DEFAULT} /> : null}
    </>
  );
}

/** 条件の帯の小さな窓（#258）：未定の理由と予定日（任意）・変更協議の文 */
function BandDialog({ dlg, setDlg, readOnly, setT, tbdOf, defaultNego }: {
  dlg: BandDlg;
  setDlg: (d: BandDlg | null) => void;
  readOnly: boolean;
  setT: (p: Partial<OrderTerms>, immediate?: boolean) => void;
  tbdOf: (k: OrderTbdKey) => OrderTerms["tbd"][OrderTbdKey];
  defaultNego: string;
}) {
  const close = () => setDlg(null);
  const title = dlg.kind === "tbd" ? ORDER_MSG.tbdNoteTitle : ORDER_MSG.negotiation;
  const done = () => {
    if (dlg.kind === "tbd") {
      if (tbdOf(dlg.key)) setT({ tbd: { ...currentTbd(), [dlg.key]: { reason: dlg.reason.trim(), on: dlg.on } } });
    } else setT({ negotiation: dlg.text });
    close();
  };
  // 窓を開いた時点の未定の組（決定で理由と予定日だけ差し替える）
  const currentTbd = () => {
    const out: OrderTerms["tbd"] = {};
    for (const k of ["receive", "place", "inspect", "pay"] as const) {
      const v = tbdOf(k);
      if (v) out[k] = v;
    }
    return out;
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="dlg obanddlg" role="dialog" aria-label={title} onKeyDown={(e) => e.key === "Escape" && close()} data-testid={dlg.kind === "tbd" ? "tbd-dialog" : "negotiation-dialog"}>
        <div className="dh">{title}</div>
        <div className="db">
          {dlg.kind === "tbd" ? (
            <>
              <Field id="otbd_reason" label={ORDER_MSG.tbdReason}>
                <input id="otbd_reason" className="inp" placeholder=" " maxLength={200} value={dlg.reason} readOnly={readOnly} autoFocus onChange={(e) => setDlg({ ...dlg, reason: e.target.value })} data-testid="tbd-reason" />
              </Field>
              <span className="olbl">{ORDER_MSG.tbdOn}</span>
              <DateInput label={ORDER_MSG.tbdOn} ariaLabel={ORDER_MSG.tbdOn} value={dlg.on ?? ""} readOnly={readOnly} onChange={(v) => setDlg({ ...dlg, on: v || null })} testId="tbd-on" />
            </>
          ) : (
            <>
              <textarea id="oc_negotiation" className="inp" rows={5} maxLength={1000} aria-label={ORDER_MSG.negotiation} value={dlg.text} readOnly={readOnly} autoFocus onChange={(e) => setDlg({ ...dlg, text: e.target.value })} style={{ width: "100%" }} data-testid="oc-negotiation" />
              {!readOnly && dlg.text !== defaultNego ? (
                <button type="button" className="btn text" onClick={() => setDlg({ ...dlg, text: defaultNego })} data-testid="oc-negotiation-reset">{ORDER_SETTINGS_MSG.negotiationReset}</button>
              ) : null}
            </>
          )}
        </div>
        <div className="df">
          <span className="sp" />
          {readOnly ? (
            <button type="button" className="btn" onClick={close}>閉じる</button>
          ) : (
            <>
              <button type="button" className="btn quiet" onClick={close}>取消</button>
              <button type="button" className="btn primary" onClick={done} data-testid="band-dialog-save">決定</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 送信設定の帯（明細の下。押すと上へ開く。Figma ①-3）。承諾の方法は発行の窓（#253）。リンクで送るのは電子のときだけ
 * ========================================================================== */
function SendBand({ o, prefs, upd, open, setOpen, field, ack }: { o: Order; prefs: OrderSendPrefs; upd: (p: Partial<OrderSendPrefs>) => void; open: boolean; setOpen: (v: boolean) => void; field: boolean; ack: OrderAckMode }) {
  const linkOk = ack === "electronic";
  const via = prefs.via === "link" && !linkOk ? "print" : prefs.via;
  const summary = via === "link" ? ORDER_MSG.viaLink : via === "mail" ? ORDER_MSG.viaMail : ORDER_MSG.viaPrint;
  return (
    <div className={"paper osend" + (open ? " is-open" : "")} data-testid="order-send-band">
      {open ? (
        <div className="osendgrid" data-testid="order-send-open">
          <div className="oscol">
            <span className="olbl">{ORDER_MSG.to}</span>
            <span data-testid="order-send-to">
              {o.client.name}
              {o.client.contact ? `　${o.client.contact.name} 様` : ""}
            </span>
          </div>
          <div className="oscol">
            <span className="olbl" id="osend-via">{ORDER_MSG.via}</span>
            <Segmented
              labelledBy="osend-via"
              value={via}
              onChange={(v) => upd({ via: v })}
              options={[
                { value: "print", label: ORDER_MSG.viaPrint, testId: "osend-via-print" },
                { value: "mail", label: ORDER_MSG.viaMail, testId: "osend-via-mail" },
                ...(linkOk ? [{ value: "link" as const, label: ORDER_MSG.viaLink, testId: "osend-via-link" }] : []),
              ]}
            />
          </div>
          <div className="oscol">
            <span className="olbl">{ORDER_MSG.print}</span>
            {field || o.lines.some((l) => l.laborBreakdown) ? <Switch checked={prefs.printBreakdown} onChange={(v) => upd({ printBreakdown: v })} label={ORDER_MSG.printBreakdown} testId="osend-print-breakdown" /> : null}
            <Switch checked={prefs.printSeal} onChange={(v) => upd({ printSeal: v })} label={ORDER_MSG.printSeal} testId="osend-print-seal" />
          </div>
          <div className="oscol">
            <Switch checked={prefs.remember} onChange={(v) => upd({ remember: v })} label={ORDER_MSG.remember} testId="osend-remember" />
            <button type="button" className="btn text sm" onClick={() => setOpen(false)}>{ORDER_MSG.collapse}</button>
          </div>
        </div>
      ) : null}
      <button type="button" className="osendhead" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="order-send-toggle">
        <b>{ORDER_MSG.sendSettings}</b>
        <span className="muted" data-testid="order-send-summary">{summary}</span>
        <span className="sp" />
        <span aria-hidden="true">{open ? "⌄" : "⌃"}</span>
      </button>
    </div>
  );
}

/* ============================================================================
 * 発行前の確認（§5.2・Figma ①-4）。承諾は選ばず「{orderAck}　書面」の1行で見せる（変えるのは条件の帯＝#257）。
 * 収入印紙の参考は 書面・請負・税抜 1万円以上 のときだけ（#252。契約の orderStampNeeded）
 * ========================================================================== */
type IssueDlg = { check: OrderCheck; company: string; busy: boolean; error?: string | undefined; diff?: OrderRevisionDiff | undefined };
function IssueDialog({ o, dlg, setDlg, needsCompany, ack, onIssue, onGo }: {
  o: Order;
  dlg: IssueDlg;
  setDlg: (d: IssueDlg | null) => void;
  needsCompany: boolean;
  ack: OrderAckMode;
  onIssue: () => void;
  onGo: (a: OrderCheckItem["action"]) => void;
}) {
  const by = (lv: OrderCheckItem["level"]) => dlg.check.items.filter((i) => i.level === lv);
  const fix = by("fix");
  const warn = by("warn");
  const stamp = orderStampNeeded({ ack, construction: o.construction, workKind: o.workKind, subtotal: o.revision.subtotal });
  const info = [...by("info").filter((i) => i.code !== "stamp"), ...(stamp ? [{ level: "info" as const, code: "stamp", text: ORDER_CHECK_MSG.stamp, action: null }] : [])];
  const close = () => !dlg.busy && setDlg(null);
  const group = (lv: OrderCheckItem["level"], items: OrderCheckItem[], chip: string, cls: string, lead?: string) =>
    items.length ? (
      <div className={`ocheck ocheck-${lv}`} data-testid={`order-check-${lv}`}>
        <div className="row" style={{ alignItems: "center", gap: 8 }}>
          <span className={`chip ${cls}`}>{chip.replace("{n}", String(items.length))}</span>
          {lead ? <b>{lead}</b> : null}
        </div>
        <ul>
          {items.map((i) => (
            <li key={i.code} data-testid={`order-check-item-${i.code}`}>
              <span>{t(i.text)}</span>
              {lv === "fix" && i.action ? (
                <button type="button" className="btn sm" onClick={() => onGo(i.action)} data-testid={`order-check-go-${i.code}`}>
                  {i.action === "terms" ? ORDER_MSG.termsAttach : ORDER_CHECK_MSG.input}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="dlg" role="dialog" aria-label={ORDER_MSG.issueTitle} onKeyDown={(e) => e.key === "Escape" && close()} data-testid="order-issue-dialog">
        <div className="dh">{ORDER_MSG.issueTitle}</div>
        <div className="db">
          <p className="muted" style={{ margin: 0 }}>
            {o.names.order}　{o.client.name} {o.client.honorific || "御中"}　合計 {yen(o.revision.total)}円
          </p>
          {/* #278：番号のある下書き（発行を取消した）は、同じ番号で再発行することを本文の1行で（訂正版の回は枝番が新しいので出さない） */}
          {o.documentNo && !o.revising ? <p style={{ margin: 0 }} data-testid="issue-reissue">{unissuedReissue(o.documentNo)}</p> : null}
          {/* {orderAck}（#257）：この1回の選び（条件の帯）を1行で見せる。オフは「なし」 */}
          <p className="oissue-ack" data-testid="order-issue-ack">
            <span className="olbl">{o.names.orderAck}</span>
            <span data-testid="order-issue-ack-value">{ORDER_ACK_LABEL[ack]}</span>
            {ack === "electronic" ? <span className="muted">{ORDER_MSG.identity}</span> : null}
          </p>
          {/* 訂正版（#233 2b）：前回とのちがい（条件の変更を1行ずつ＋明細）。前回と同じ内容は発行できない */}
          {dlg.diff ? (
            <div data-testid="order-revise-diff">
              <div className="rvh" style={{ fontWeight: 700, margin: "4px 0" }}>{ORDER_REVISE_MSG.diffTitle}</div>
              <OrderChanges
                prev={dlg.diff.base.displayNo}
                no={dlg.diff.next.displayNo}
                prevTotal={dlg.diff.base.total}
                total={dlg.diff.next.total}
                recordNo={dlg.diff.base.acceptance?.recordNo ?? null}
                terms={dlg.diff.terms}
                rows={dlg.diff.rows}
                unchanged={dlg.diff.unchangedCount}
                testId="order-diff"
              />
            </div>
          ) : null}
          {group("fix", fix, ORDER_CHECK_MSG.fixChip, "shu", ORDER_CHECK_MSG.fixLead)}
          {group("warn", warn, ORDER_CHECK_MSG.warnChip, "shu-ring", ORDER_CHECK_MSG.warnLead)}
          {group("info", info, ORDER_CHECK_MSG.infoChip, "quiet")}
          {needsCompany ? (
            <Field id="oi_co" label="会社名（屋号・お名前でも）">
              <input id="oi_co" className="inp" autoComplete="organization" value={dlg.company} onChange={(e) => setDlg({ ...dlg, company: e.target.value, error: undefined })} />
            </Field>
          ) : null}
          <ErrorText>{dlg.error}</ErrorText>
        </div>
        <div className="df">
          {fix.length ? <span className="muted" data-testid="order-check-blocked">{t(ORDER_MSG.issueBlocked).replace("{n}", String(fix.length))}</span> : null}
          <span className="sp" />
          <button type="button" className="btn quiet" disabled={dlg.busy} onClick={close}>取消</button>
          <button type="button" className="btn primary" disabled={dlg.busy || fix.length > 0 || !!dlg.diff?.empty} onClick={onIssue} data-testid="order-issue-confirm">
            {o.revising && o.displayNo ? ORDER_REVISE_MSG.issue.replace("{no}", o.displayNo) : ORDER_MSG.issueConfirm}
          </button>
        </div>
      </div>
    </div>
  );
}

export { mdSlash };
